import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '../Chip';
import { Identity } from '../Header/interfaces';
import SearchOptionCard from './SearchOptionCard';
import { getFullName } from '@octanner/prism-utilities';
import classNames from 'classnames';
import {
  CustomPaper,
  getSearchInputStyles,
  StyledCloseIcon,
  TextFieldContainer,
} from './styles';
import { Props } from './types';
const DEFAULT_OPTIONS: Identity[] = [];

// avoid passing down onchange prop for pagefilter
const Search = <
  OptionType extends object = Identity,
  Multiple extends boolean = true,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = true,
>({
  autoFocus = false,
  required = false,
  error,
  freeSolo,
  helperText,
  label,
  loading,
  multiple,
  options,
  placeholder,
  inputDataTestId,
  disabled,
  value,
  clearIcon,
  onChange,
  ...props
}: Props<OptionType, Multiple, DisableClearable, FreeSolo>) => {
  return (
    <Autocomplete<OptionType, Multiple, DisableClearable, FreeSolo>
      onChange={onChange}
      clearIcon={
        clearIcon ?? <StyledCloseIcon size={props.size} tabIndex={0} />
      }
      filterOptions={(options) => options}
      freeSolo={freeSolo ?? (true as FreeSolo)}
      getOptionLabel={
        multiple ? undefined : getIdentityProperty('getOptionLabel')
      }
      multiple={multiple ?? (true as Multiple)}
      options={options ?? (DEFAULT_OPTIONS as OptionType[])}
      disabled={disabled}
      renderTags={(options, getTagProps) =>
        options.map((option, index) => (
          <Chip
            label={getIdentityProperty('renderTags')(option)}
            {...getTagProps({ index })}
          />
        ))
      }
      renderOption={(props, option) => (
        <SearchOptionCard
          {...props}
          avatarUrl={getIdentityProperty('renderOption')(option, 'avatarUrl')}
          showAvatar
          subTitle={getIdentityProperty('renderOption')(option, 'buName')}
          title={getIdentityProperty('renderOption')(option)}
        />
      )}
      forcePopupIcon={false}
      PaperComponent={CustomPaper}
      renderInput={(props) => (
        <TextFieldContainer
          {...props}
          // eslint-disable-next-line
          autoFocus={autoFocus}
          error={error}
          helperText={helperText}
          label={label}
          placeholder={
            Array.isArray(value) && Boolean(value?.length) ? '' : placeholder
          }
          variant="outlined"
          required={required}
          inputProps={{
            ...props.inputProps,
            'data-testid': inputDataTestId,
            className: classNames('prism-search', props.inputProps.className),
            onKeyDown: (e) => {
              if (
                e.key === 'Enter' &&
                (options?.length === 0 || loading || !onChange)
              ) {
                e.stopPropagation();
              }
            },
          }}
          InputProps={getSearchInputStyles(disabled, props, loading)}
        />
      )}
      value={value}
      {...props}
    />
  );
};
export default Search;

export const getIdentityProperty =
  (type: string) => (option: any, value?: 'avatarUrl' | 'buName') => {
    if (isIdentity(option)) {
      switch (value) {
        case 'avatarUrl':
          return option.profile?.avatar || '';
        case 'buName':
          return option.businessUnit?.buName || '';
        default:
          return getFullName(option);
      }
    }
    return `${option}`;
  };

const isIdentity = (option: any): option is Identity =>
  (option as Identity).firstName !== undefined;
